import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * AliasManager — admin/manager UI to teach the fuzzy matcher operator
 * shortcuts. e.g. "pwd" → "powder", "malto" → "maltodextrin".
 *
 * Aliases are applied as whole-word replacements BEFORE the fuzzy match
 * runs on chat tools, voice order parsing, and purchase-bill OCR — so
 * an operator typing "garam mas pwd" will resolve to "Garam Masala
 * Powder" instantly.
 *
 * Renders standalone at /aliases.
 */
export default function AliasManager() {
  const [rows, setRows] = useState([]);
  const [alias, setAlias] = useState('');
  const [expandsTo, setExpandsTo] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/aliases/`, { withCredentials: true });
      setRows(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!alias.trim() || !expandsTo.trim()) { toast.error('Both fields required'); return; }
    try {
      await axios.post(`${API}/aliases/`, { alias: alias.trim(), expands_to: expandsTo.trim() }, { withCredentials: true });
      toast.success(`Alias "${alias}" saved`);
      setAlias(''); setExpandsTo('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete alias "${a}"?`)) return;
    try {
      await axios.delete(`${API}/aliases/${encodeURIComponent(a)}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="aliases-page">
      <div>
        <h1 className="text-2xl font-bold text-zinc-100">Fuzzy Match Aliases</h1>
        <p className="text-zinc-500 text-sm mt-1">
          Teach the AI your shortcuts. E.g. <code className="text-amber-400">pwd</code> → <code className="text-amber-400">powder</code>.
          Used in chat commands, voice order parsing, and purchase-bill OCR.
        </p>
      </div>

      <form onSubmit={save} className="flex flex-wrap items-end gap-3 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="alias-form">
        <div className="flex-1 min-w-[160px]">
          <label className="text-zinc-500 text-[11px] block mb-1">Shortcut</label>
          <input value={alias} onChange={e => setAlias(e.target.value)}
            placeholder="e.g. pwd"
            data-testid="alias-input"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500" />
        </div>
        <div className="text-zinc-500 mb-1.5">→</div>
        <div className="flex-1 min-w-[160px]">
          <label className="text-zinc-500 text-[11px] block mb-1">Expands to</label>
          <input value={expandsTo} onChange={e => setExpandsTo(e.target.value)}
            placeholder="e.g. powder"
            data-testid="expansion-input"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500" />
        </div>
        <button type="submit" data-testid="save-alias-btn"
          className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold px-4 py-2 rounded-lg">
          <Plus size={14}/> Add
        </button>
      </form>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-6 text-zinc-500 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-zinc-500 text-sm">No aliases yet. Add one above to teach the fuzzy matcher.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-800/40">
              <tr className="text-zinc-500 text-[10px] uppercase tracking-wider">
                <th className="text-left px-4 py-2">Shortcut</th>
                <th className="text-left px-4 py-2">Expands To</th>
                <th className="text-left px-4 py-2">Added By</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-zinc-800/40" data-testid={`alias-row-${r.alias}`}>
                  <td className="px-4 py-2 font-mono text-amber-400" title={`Match key (case-insensitive): ${r.alias}`}>
                    {r.display || r.alias}
                  </td>
                  <td className="px-4 py-2 text-zinc-200">{r.expands_to}</td>
                  <td className="px-4 py-2 text-zinc-500 text-[10px]">{r.created_by || '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => remove(r.alias)} data-testid={`delete-alias-${r.alias}`}
                      className="p-1.5 rounded bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30">
                      <Trash2 size={11}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
